import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { api, fmtNGN, formatErr, fmtDate } from "@/lib/api";
import { Bank, FileText, ShieldCheck, Lightning, CircleNotch, Brain } from "@phosphor-icons/react";
import { toast } from "sonner";
import LoanInvoicePanel from "@/components/LoanInvoicePanel";
import PayoutControl from "@/components/PayoutControl";

// Filters that map to backend state machine for the new 7-stage workflow.
// Admin's role here is the two-eyes disbursement step only — underwriting (final-approve,
// dd-verify) is performed by the credit_reviewer role on /approver/loans.
const FILTERS = [
  { key: "queue",     label: "Disburse queue", hint: "Loans cleared by credit officer, awaiting admin disbursement" },
  { key: "dd_verified", label: "Ready to disburse", hint: "DD verified — sign off and pay the school" },
  { key: "disbursed",   label: "Disbursed",     hint: "Already paid to school" },
  { key: "underwriting", label: "Underwriting",hint: "In flight with credit officer" },
  { key: "all",         label: "All loans",    hint: "Full ledger" },
];

const STATE_BADGE = {
  consent_pending:  ["badge-neutral", "Consent"],
  scoring:          ["badge-neutral", "Scoring"],
  aip_offered:      ["badge-info",    "AIP offered"],
  aip_accepted:     ["badge-warning", "Awaiting officer"],
  final_approved:   ["badge-info",    "Offer ready"],
  offer_accepted:   ["badge-warning", "DD setup"],
  ispo_pending:     ["badge-warning", "ISPO awaiting signature"],
  ispo_accepted:    ["badge-warning", "DD setup"],
  dd_setup_pending: ["badge-warning", "DD verifying"],
  dd_verified:      ["badge-info",    "Ready to disburse"],
  disbursed:        ["badge-success", "Disbursed"],
  declined:         ["badge-danger",  "Declined"],
  approved:         ["badge-success", "Approved"],
  pending:          ["badge-warning", "Pending"],
};

const ACTIONABLE_STATES = ["dd_verified"]; // admin only disburses; underwriting belongs to credit approver
const UNDERWRITING_STATES = ["aip_offered", "aip_accepted", "final_approved", "offer_accepted", "dd_setup_pending"];

export default function AdminLoans() {
  const [loans, setLoans] = useState([]);
  const [filter, setFilter] = useState("queue");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/loans/all");
      setLoans(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return loans;
    if (filter === "queue") return loans.filter((l) => ACTIONABLE_STATES.includes(l.state));
    if (filter === "underwriting") return loans.filter((l) => UNDERWRITING_STATES.includes(l.state));
    return loans.filter((l) => l.state === filter);
  }, [loans, filter]);

  const counts = useMemo(() => {
    const c = { queue: 0, all: loans.length, underwriting: 0 };
    for (const l of loans) {
      if (ACTIONABLE_STATES.includes(l.state)) c.queue += 1;
      if (UNDERWRITING_STATES.includes(l.state)) c.underwriting += 1;
      c[l.state] = (c[l.state] || 0) + 1;
    }
    return c;
  }, [loans]);

  return (
    <DashboardShell
      title="Loan underwriting"
      subtitle="Review AI-scored applications, issue final offers, verify Direct Debits, and disburse."
      actions={
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`btn-ghost text-sm ${filter === f.key ? "!bg-[#0A3622] !text-white" : ""}`}
              data-testid={`loans-filter-${f.key}`}
            >
              {f.label}
              <span className="ml-2 inline-flex items-center justify-center px-1.5 min-w-[20px] h-[20px] rounded-full bg-white/30 text-[11px] font-mono">
                {counts[f.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
      }
    >
      <div className="card">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-8 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-8 text-center" data-testid="loans-empty">
            No loans in this view.
          </p>
        ) : (
          <div className="space-y-4">
            {filtered.map((l) => (
              <LoanCard
                key={l.id}
                loan={l}
                expanded={expanded === l.id}
                onToggle={() => setExpanded(expanded === l.id ? null : l.id)}
                onChange={reload}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function LoanCard({ loan, expanded, onToggle, onChange }) {
  const score = loan.ai_risk_score || 500;
  const color = score >= 700 ? "#2D7A5D" : score >= 450 ? "#B45309" : "#C93B3B";
  const [bc, label] = STATE_BADGE[loan.state] || ["badge-neutral", loan.state || "—"];

  return (
    <div className="border border-[#E5E3DB] rounded-2xl overflow-hidden" data-testid={`admin-loan-card-${loan.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid lg:grid-cols-12 gap-4 items-center p-5 text-left hover:bg-[#F9F7F4] transition-colors"
        data-testid={`admin-loan-toggle-${loan.id}`}
      >
        <div className="lg:col-span-2 flex items-center gap-4">
          <Gauge value={score} color={color} />
          <div>
            <div className="font-display text-lg font-medium">Band {loan.ai_risk_band || "—"}</div>
            <div className="text-xs text-[#4F5D4F]">{score}/1000</div>
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="font-display font-medium text-[#132A13]">{fmtNGN(loan.amount)}</div>
          {loan.school_name && <div className="text-xs text-[#0A3622] font-semibold truncate" data-testid={`loan-school-${loan.id}`}>{loan.school_name}{loan.invoice_id ? " · invoice" : ""}</div>}
          <div className="text-xs text-[#4F5D4F]">
            {loan.tenor_months} mo · {loan.monthly_rate_pct ?? (loan.interest_bps / 1200).toFixed(2)}% / month
          </div>
        </div>
        <div className="lg:col-span-3 text-sm">
          <div className="text-[#4F5D4F]">
            Income: <span className="text-[#132A13] font-medium">{fmtNGN(loan.monthly_income)}/mo</span>
          </div>
          <div className="text-[#4F5D4F]">
            Dependents: <span className="text-[#132A13] font-medium">{loan.dependents}</span> · {loan.employment_type}
          </div>
        </div>
        <div className="lg:col-span-2 text-xs text-[#4F5D4F]">
          <div>Applied {fmtDate(loan.created_at)}</div>
          <div className="line-clamp-1">AI: {loan.ai_recommendation || "—"}</div>
        </div>
        <div className="lg:col-span-2 flex justify-end">
          <span className={`badge ${bc}`} data-testid={`admin-loan-state-${loan.id}`}>{label}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#E5E3DB] p-5 bg-[#FAFAF7] grid md:grid-cols-2 gap-5">
          <DetailPanel loan={loan} onChange={onChange} />
          <ActionPanel loan={loan} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

function DetailPanel({ loan, onChange }) {
  const stmt = loan.statements_summary || {};
  const accounts = loan.bank_discovery?.accounts || [];
  return (
    <div className="space-y-4 text-sm" data-testid={`detail-panel-${loan.id}`}>
      <LoanInvoicePanel loan={loan} canCheck={false} onChange={onChange} />
      <Section icon={<Brain size={14} weight="fill" />} title="AI assessment">
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          <div>Score: <span className="font-mono font-semibold">{loan.ai_risk_score}/1000</span></div>
          <div>Band: <span className="font-mono font-semibold">{loan.ai_risk_band}</span></div>
          <div>Recommendation: <span className="font-mono font-semibold">{loan.ai_recommendation}</span></div>
          <div>Risk-priced rate: <span className="font-mono font-semibold">{loan.monthly_rate_pct ?? (loan.interest_bps / 1200).toFixed(2)}% / month</span></div>
        </div>
        {loan.ai_reasons?.length > 0 && (
          <ul className="mt-2.5 space-y-1 text-xs text-[#4F5D4F]">
            {loan.ai_reasons.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        )}
      </Section>

      <Section icon={<Bank size={14} weight="fill" />} title={`Bank discovery (${accounts.length} accounts)`}>
        {accounts.length === 0 ? (
          <p className="text-xs text-[#4F5D4F]">No discovered accounts.</p>
        ) : (
          <ul className="text-xs font-mono space-y-1">
            {accounts.map((a) => (
              <li key={a.account_number} className="flex justify-between">
                <span>{a.bank_name}</span>
                <span className="text-[#4F5D4F]">****{a.account_number.slice(-4)}</span>
              </li>
            ))}
          </ul>
        )}
        {Object.keys(stmt).length > 0 && (
          <div className="grid grid-cols-2 gap-1.5 text-xs mt-3 pt-3 border-t border-[#E5E3DB]">
            <div>Avg inflow: <span className="font-mono font-semibold">{fmtNGN(stmt.avg_inflow_ngn)}</span></div>
            <div>Avg outflow: <span className="font-mono font-semibold">{fmtNGN(stmt.avg_outflow_ngn)}</span></div>
            <div>Salary consistency: <span className="font-mono font-semibold">{((stmt.salary_consistency || 0) * 100).toFixed(0)}%</span></div>
            <div>Overdrafts: <span className="font-mono font-semibold">{stmt.overdraft_count}</span></div>
          </div>
        )}
      </Section>

      {loan.dd_account && (
        <Section icon={<ShieldCheck size={14} weight="fill" />} title="Direct Debit mandate">
          <div className="text-xs space-y-1 font-mono">
            <div>Bank: <span className="font-semibold">{loan.dd_account.bank_name}</span></div>
            <div>Account: <span className="font-semibold">****{loan.dd_account.account_number?.slice(-4)}</span></div>
            <div>Mandate ref: <span className="font-semibold text-[#0A3622]">{loan.dd_account.mandate_reference}</span></div>
            <div>Submitted: {fmtDate(loan.dd_account.submitted_at)}</div>
          </div>
        </Section>
      )}

      <Section icon={<FileText size={14} weight="fill" />} title="State history">
        <ol className="text-xs space-y-1">
          {(loan.events || []).slice(-8).map((e, i) => (
            <li key={i} className="flex justify-between font-mono">
              <span>{e.state}</span>
              <span className="text-[#4F5D4F]">{fmtDate(e.at)} · {e.by}</span>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E3DB] p-4">
      <div className="flex items-center gap-2 mb-2.5 text-[#0A3622]">
        {icon}
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function ActionPanel({ loan, onChange }) {
  // Admin-only: disbursement (two-eyes step). Underwriting actions (final-approve, dd-verify)
  // are surfaced in the Credit Approver console at /approver/loans.
  if (loan.state === "dd_verified") return <DisburseAction loan={loan} onChange={onChange} />;
  if (loan.state === "aip_accepted") {
    return (
      <ActionShell title="With credit officer">
        <p className="text-xs text-[#4F5D4F]">Borrower has accepted the AIP. Waiting for credit officer to issue the binding Loan Offer Letter.</p>
      </ActionShell>
    );
  }
  if (loan.state === "dd_setup_pending") {
    return (
      <ActionShell title="DD verification pending">
        <p className="text-xs text-[#4F5D4F]">Borrower submitted DD mandate <span className="font-mono">{loan.dd_account?.mandate_reference}</span>. Credit officer is verifying.</p>
      </ActionShell>
    );
  }
  if (loan.state === "aip_offered") {
    return (
      <ActionShell title="Awaiting borrower">
        <p className="text-xs text-[#4F5D4F]">AIP issued. Awaiting borrower acceptance.</p>
      </ActionShell>
    );
  }
  if (loan.state === "final_approved") {
    return (
      <ActionShell title="Awaiting borrower">
        <p className="text-xs text-[#4F5D4F]">Offer letter sent. Awaiting borrower acceptance.</p>
      </ActionShell>
    );
  }
  if (loan.state === "offer_accepted") {
    return (
      <ActionShell title="Awaiting Direct Debit">
        <p className="text-xs text-[#4F5D4F]">Borrower accepted offer; setting up DD with their bank.</p>
      </ActionShell>
    );
  }
  if (loan.state === "disbursed") {
    return (
      <ActionShell title="Loan disbursed">
        <p className="text-xs text-[#4F5D4F]">{fmtNGN(loan.amount)} disbursed on {fmtDate(loan.disbursed_at)}{loan.disbursed_to?.school_name ? ` to ${loan.disbursed_to.school_name}` : ""}.</p>
        {loan.payout?.transfer_code && <p className="text-xs text-[#4F5D4F] mt-1" data-testid={`loan-transfer-${loan.id}`}>Paystack transfer <span className="font-mono">{loan.payout.transfer_code}</span> · {loan.payout.status}</p>}
      </ActionShell>
    );
  }
  if (loan.state === "declined") {
    return (
      <ActionShell title="Declined">
        <p className="text-xs text-[#4F5D4F]">{loan.declined_by ? `Declined by ${loan.declined_by}` : "Declined."}</p>
      </ActionShell>
    );
  }
  if (loan.status === "pending") {
    return (
      <ActionShell title="Awaiting credit officer">
        <p className="text-xs text-[#4F5D4F]">Application is in the credit-officer queue.</p>
      </ActionShell>
    );
  }
  return (
    <ActionShell title="No action">
      <p className="text-xs text-[#4F5D4F]">State: <span className="font-mono">{loan.state || "—"}</span></p>
    </ActionShell>
  );
}

function ActionShell({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E3DB] p-4">
      <div className="overline mb-2">{title}</div>
      {children}
    </div>
  );
}


function DisburseAction({ loan, onChange }) {
  return (
    <ActionShell title="Disburse to school">
      <p className="text-xs text-[#4F5D4F] mb-3">
        Two-eyes step. {fmtNGN(loan.amount)} is sent by Paystack transfer to {loan.school_name || "the school"}'s bank account, never to the parent.{loan.remark ? ` Child: ${loan.remark}.` : ""}
      </p>
      {loan.invoice_id && loan.invoice_check?.status !== "approved" ? (
        <p className="text-xs text-[#B45309]" data-testid={`disburse-blocked-${loan.id}`}>Waiting for a credit officer to approve the school invoice before payout.</p>
      ) : (
        <PayoutControl payout={loan.payout} amountLabel={fmtNGN(loan.amount)} testid={`disburse-${loan.id}`}
          disbursePath={`/admin/loans/${loan.id}/disburse`} otpPath={`/admin/loans/${loan.id}/disburse/otp`} onDone={onChange} />
      )}
    </ActionShell>
  );
}


function Gauge({ value, color }) {
  const pct = Math.min(value, 1000) / 1000;
  return (
    <svg viewBox="0 0 36 36" className="w-14 h-14">
      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E3DB" strokeWidth="3" />
      <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${pct * 100} 100`} strokeLinecap="round" transform="rotate(-90 18 18)" />
    </svg>
  );
}
